import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnDestroy,
} from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { MatDialog } from "@angular/material/dialog";
import { ClientTableRow, PlanGroup, PlanType } from "./client-table.interface";
import { Observable, Subject } from "rxjs";
import { debounceTime, takeUntil, distinctUntilChanged } from "rxjs/operators";
import { Router } from "@angular/router";
import { StoreExtendedService } from "../store-extended.service";
import {
  CHANGE_ACCOUNT_SUCCESS,
  ChangeAccountAction,
} from "../../accounts/accounts.actions";
import { ClientWithStats } from "../../client/client.interface";
import { TranslateService } from "@ngx-translate/core";
import { ReportsService } from "../reports/reports.service";
import { AddSubdomainDialogComponent } from "../../client/add-subdomain-dialog/add-subdomain-dialog.component";

const PLAN_CONFIG: Record<PlanType, { name: string; icon: string }> = {
  paid: { name: "Paid", icon: "payments" },
  partner: { name: "Partner", icon: "handshake" },
  free: { name: "Free", icon: "free_breakfast" },
  freemium: { name: "Freemium", icon: "free_breakfast" },
  reseller: { name: "Resellers", icon: "store" },
} as const;

const SEARCH_DEBOUNCE_TIME = 300;
const DEFAULT_PLAN_TYPE: PlanType = "free";
const ALWAYS_EXPANDED_PLANS: ReadonlySet<PlanType> = new Set([
  "free",
  "freemium",
]);

@Component({
  selector: "app-client-table",
  templateUrl: "./client-table.component.html",
  styleUrls: ["./client-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ClientTableComponent implements OnInit, OnDestroy {
  @Input() data!: Observable<ClientTableRow[]>;
  @Input() actionsTemplate?: TemplateRef<unknown>;
  @Output() reloadRequested = new EventEmitter<void>();
  @Output() rowActivate = new EventEmitter<ClientWithStats>();
  @ViewChild(MatSort) sort!: MatSort;

  readonly displayedColumns = [
    "expand",
    "name",
    "googleAdminEmail",
    "createdAt",
    "stats",
    "planId",
    "trialFinishDate",
    "billingType",
    "addSubdomain",
    "actions",
  ];

  planGroups: PlanGroup[] = [];
  loading = true;
  searchTerm = "";

  get isEmpty(): boolean {
    return !this.loading && this.clients.size === 0 && !this.isFilterActive;
  }

  get isFilterActive(): boolean {
    return Boolean(this.searchTerm.trim());
  }

  get showNoSearchResults(): boolean {
    if (!this.loading && this.isFilterActive) {
      return this.planGroups.every((group) => group.filteredCount === 0);
    }
    return false;
  }

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();
  private readonly expandedDomains = new Set<string>();
  private readonly expandedPlans = new Set<PlanType>(ALWAYS_EXPANDED_PLANS);
  private readonly clients = new Map<string, ClientWithStats>();
  private readonly subdomainsCache = new Map<
    string,
    readonly ClientWithStats[]
  >();
  private readonly searchCache = new Map<string, boolean>();
  private readonly planOrder: readonly PlanType[] = [
    "paid",
    "partner",
    "reseller",
    "free",
    "freemium",
  ];

  constructor(
    private readonly store: StoreExtendedService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly reportsService: ReportsService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.setupSearchStream();
  }

  ngOnInit(): void {
    this.data.pipe(takeUntil(this.destroy$)).subscribe({
      next: (clients) => {
        this.processClientsData(clients);
      },
      error: (error) => {
        console.error("Failed to load clients data:", error);
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
    this.updateDisplayedColumns();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilter = (event: Event): void => {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  };

  setPlanExpanded = (planId: PlanType, expanded: boolean): void => {
    if (ALWAYS_EXPANDED_PLANS.has(planId)) {
      this.expandedPlans.add(planId);
      const group = this.planGroups.find((g) => g.planId === planId);
      if (group) {
        group.isExpanded = true;
      }
      this.refreshPlanRows();
      this.cdr.markForCheck();
      return;
    }

    if (expanded) {
      this.expandedPlans.add(planId);
    } else {
      this.expandedPlans.delete(planId);
    }

    const group = this.planGroups.find((g) => g.planId === planId);
    if (group) {
      group.isExpanded = expanded;
    }

    this.refreshPlanRows();
    this.cdr.markForCheck();
  };

  toggleExpand = (domain: string): void => {
    if (this.expandedDomains.has(domain)) {
      this.expandedDomains.delete(domain);
    } else {
      this.expandedDomains.add(domain);
    }
    this.refreshPlanRows();
    this.cdr.markForCheck();
  };

  clientDetailLink(row: ClientWithStats): string[] | null {
    const clientId = row.clientId?.trim();
    return clientId ? ["/admin/client", clientId] : null;
  }

  goToClient = (domain: string): void => {
    this.store.waitForAction(CHANGE_ACCOUNT_SUCCESS).subscribe(() => {
      void this.router.navigate(["signatures"]);
    });
    this.store.dispatch(new ChangeAccountAction(domain));
  };

  generateReport = (): void => {
    const clientsArray = Array.from(this.clients.values());
    this.reportsService.generateClientsReport(clientsArray);
  };

  addSubdomain = (client: ClientWithStats): void => {
    const dialogRef = this.dialog.open(AddSubdomainDialogComponent, {
      data: {
        clientId: client.clientId,
        domain: client.domain,
        planId: client.planId || "",
      },
      panelClass: "app-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.reloadRequested.emit();
      }
    });
  };

  isPlanExpanded = (planId: PlanType): boolean =>
    this.expandedPlans.has(planId) || ALWAYS_EXPANDED_PLANS.has(planId);

  isExpanded = (domain: string): boolean => this.expandedDomains.has(domain);

  isSubdomain = (element: ClientWithStats): boolean =>
    Boolean(element.parentDomain);

  hasSubdomains = (domain: string): boolean =>
    this.getSubdomains(domain).length > 0;

  shouldShowExpandIcon = (element: ClientWithStats): boolean =>
    !this.isSubdomain(element) && this.hasSubdomains(element.domain);

  getPlanIcon = (planId: string): string =>
    PLAN_CONFIG[planId as PlanType]?.icon || "account_circle";

  getPlanDisplayName = (planId: string): string =>
    PLAN_CONFIG[planId as PlanType]?.name || planId;

  trackByPlanId = (_: number, planGroup: PlanGroup): string => planGroup.planId;

  trackByDomain = (_: number, client: ClientWithStats): string =>
    client.clientId;

  private setupSearchStream(): void {
    this.searchSubject
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
        this.clearSearchCache();
        this.updateFilteredCounts();
        this.refreshPlanRows();
        this.cdr.markForCheck();
      });
  }

  private processClientsData(clients: ClientTableRow[]): void {
    this.loading = true;

    const previousPlanByClientId = new Map<string, string>();
    this.clients.forEach((client) => {
      if (client.clientId) {
        previousPlanByClientId.set(
          client.clientId,
          client.planId || DEFAULT_PLAN_TYPE,
        );
      }
    });

    this.clients.clear();
    clients.forEach((client) => {
      this.clients.set(client.domain, client);
      if (client.clientId) {
        const previousPlan = previousPlanByClientId.get(client.clientId);
        const nextPlan = (client.planId || DEFAULT_PLAN_TYPE) as PlanType;
        if (previousPlan && previousPlan !== nextPlan) {
          this.expandedPlans.add(nextPlan);
        }
      }
    });

    this.clearCaches();
    this.groupClientsByPlan();
    this.updateFilteredCounts();
    this.refreshPlanRows();

    this.loading = false;
    this.cdr.markForCheck();
  }

  private clearCaches(): void {
    this.subdomainsCache.clear();
    this.searchCache.clear();
  }

  private updateDisplayedColumns(): void {
    if (!this.actionsTemplate) {
      this.displayedColumns.pop();
    }
  }

  private groupClientsByPlan(): void {
    const planMap = new Map<PlanType, ClientWithStats[]>();

    Array.from(this.clients.values()).forEach((client) => {
      const planId = (client.planId || DEFAULT_PLAN_TYPE) as PlanType;
      if (!planMap.has(planId)) {
        planMap.set(planId, []);
      }
      const planClients = planMap.get(planId);
      if (planClients) {
        planClients.push(client);
      }
    });

    const previousSources = new Map(
      this.planGroups.map((group) => [group.planId, group.dataSource]),
    );

    this.planGroups = this.planOrder
      .filter((planId) => planMap.has(planId))
      .map((planId) => {
        const clients = planMap.get(planId) || [];
        const isExpanded = this.isPlanExpanded(planId);
        if (isExpanded) {
          this.expandedPlans.add(planId);
        }
        return {
          planId,
          displayName: this.getPlanDisplayName(planId),
          clients,
          isExpanded,
          count: clients.length,
          filteredCount: 0,
          dataSource:
            previousSources.get(planId) ||
            new MatTableDataSource<ClientWithStats>([]),
        };
      });
  }

  private updateFilteredCounts(): void {
    if (!this.searchTerm?.trim()) {
      this.planGroups.forEach((group) => (group.filteredCount = group.count));
      return;
    }

    const searchTerm = this.searchTerm.toLowerCase();
    this.planGroups.forEach((group) => {
      let count = 0;
      for (const client of group.clients) {
        if (!client.parentDomain) {
          if (
            this.matchesSearch(client, searchTerm) ||
            this.hasMatchingSubdomain(client.domain, searchTerm)
          ) {
            count++;
          }
        }
      }
      group.filteredCount = count;
    });
  }

  private refreshPlanRows(): void {
    this.planGroups.forEach((group) => {
      if (!group.isExpanded && !ALWAYS_EXPANDED_PLANS.has(group.planId)) {
        group.dataSource.data = [];
        return;
      }

      const rows: ClientWithStats[] = [];
      group.clients.forEach((client) => {
        if (client.parentDomain) {
          return;
        }

        if (!this.searchTerm?.trim() || this.shouldIncludeClient(client)) {
          rows.push(client);
          if (
            this.expandedDomains.has(client.domain) ||
            this.shouldShowSubdomains(client.domain)
          ) {
            rows.push(...this.getSubdomains(client.domain));
          }
        }
      });

      group.dataSource.data = rows;
    });
  }

  private shouldIncludeClient(client: ClientWithStats): boolean {
    if (!this.searchTerm?.trim()) {
      return true;
    }

    const searchTerm = this.searchTerm.toLowerCase();
    return (
      this.matchesSearch(client, searchTerm) ||
      this.hasMatchingSubdomain(client.domain, searchTerm)
    );
  }

  private matchesSearch(client: ClientWithStats, searchTerm: string): boolean {
    const searchableFields = [
      client.name,
      client.domain,
      client.clientId,
      client.googleAdminEmail,
      client.planId,
    ];

    return searchableFields.some((field) =>
      field?.toLowerCase().includes(searchTerm),
    );
  }

  private getSubdomains(domain: string): readonly ClientWithStats[] {
    const cached = this.subdomainsCache.get(domain);
    if (cached !== undefined) {
      return cached;
    }

    const subdomains = Array.from(this.clients.values()).filter(
      (client) => client.parentDomain === domain,
    );

    this.subdomainsCache.set(domain, subdomains);
    return subdomains;
  }

  private shouldShowSubdomains(domain: string): boolean {
    if (!this.searchTerm?.trim()) {
      return false;
    }

    const searchTerm = this.searchTerm.toLowerCase();
    return this.hasMatchingSubdomain(domain, searchTerm);
  }

  private hasMatchingSubdomain(domain: string, searchTerm: string): boolean {
    const cacheKey = `${domain}-${searchTerm}`;
    const cached = this.searchCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const subdomains = this.getSubdomains(domain);
    const hasMatch = subdomains.some((sub) =>
      this.matchesSearch(sub, searchTerm),
    );

    this.searchCache.set(cacheKey, hasMatch);
    return hasMatch;
  }

  private clearSearchCache(): void {
    this.searchCache.clear();
  }
}
